import type { RequestIntegrationPayload, RequestIntegrationResult } from "./types"

const ENDPOINT = "/site/integration"

declare global {
  interface Window {
    grecaptcha?: {
      ready: (cb: () => void) => void
      execute: (siteKey: string, opts: { action: string }) => Promise<string>
    }
    __RECAPTCHA_SITE_KEY__?: string
  }
}

function getCsrfToken(): string {
  const meta = document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement | null
  return meta?.content ?? ""
}

function getRecaptchaSiteKey(): string | null {
  return window.__RECAPTCHA_SITE_KEY__ ?? null
}

/**
 * Generate a reCAPTCHA v3 token (action='integration').
 * Returns null when reCAPTCHA is not configured / script absent — caller
 * decides whether to send anyway (server-side validation will catch it).
 */
export async function getRecaptchaToken(): Promise<string | null> {
  const siteKey = getRecaptchaSiteKey()
  if (!siteKey || !window.grecaptcha) return null
  await new Promise<void>((resolve) => window.grecaptcha!.ready(resolve))
  return window.grecaptcha.execute(siteKey, { action: "integration" })
}

export async function submitIntegrationRequest(
  payload: Omit<RequestIntegrationPayload, "recaptchaToken"> & { recaptchaToken?: string }
): Promise<RequestIntegrationResult> {
  const recaptchaToken = payload.recaptchaToken ?? (await getRecaptchaToken()) ?? ""

  const body = new URLSearchParams({
    "IntegrationContactForm[appName]": payload.appName,
    "IntegrationContactForm[company]": payload.company,
    "IntegrationContactForm[account]": payload.account,
    "IntegrationContactForm[comment]": payload.comment,
    "IntegrationContactForm[recaptchaToken]": recaptchaToken,
  })

  const res = await fetch(ENDPOINT, {
    method: "POST",
    credentials: "include",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/x-www-form-urlencoded",
      "X-CSRF-Token": getCsrfToken(),
      "X-Requested-With": "XMLHttpRequest",
    },
    body: body.toString(),
  })

  if (!res.ok) {
    return { success: false, errors: { _form: [`HTTP ${res.status}`] } }
  }

  const data = (await res.json()) as RequestIntegrationResult
  return data
}
